/* eslint-disable jsdoc/require-returns */
/* eslint-disable jsdoc/require-param-type */
/* eslint-disable jsdoc/check-param-names */
/* eslint-disable react/no-array-index-key */
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PropTypes from 'prop-types';

import Box from '@zenbusiness/zui/Box';
import superstyled from '@zenbusiness/zui/styles/superstyled';
import Text from '@zenbusiness/zui/Text';

/**
 * Styled Components
 */
const StatsGrid = superstyled({
  name: 'StatCardSection',
  slot: 'Grid',
  tag: Box,
  styles: ({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr'
    }
  })
});

const StatCard = superstyled({
  name: 'StatCardSection',
  slot: 'Card',
  tag: Box,
  styles: ({ theme }) => ({
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.main}`,
    borderRadius: theme.shape.rounded.lg,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1)
  })
});

const StatValue = superstyled({
  name: 'StatCardSection',
  slot: 'Value',
  tag: Text,
  styles: ({ theme }) => ({
    color: theme.palette.primary.main,
    lineHeight: 1.2
  })
});

const TrendWrapper = superstyled({
  name: 'StatCardSection',
  slot: 'TrendWrapper',
  tag: Box,
  styles: ({ theme, trendDirection }) => {
    const colorMap = {
      up: theme.palette.success.main,
      down: theme.palette.error.main,
      stable: theme.palette.text.secondary
    };
    return {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      color: colorMap[trendDirection] || theme.palette.text.secondary
    };
  }
});

/**
 * Get trend icon and direction based on trend value
 * @param trend
 */
function getTrendInfo(trend) {
  if (!trend) {
    return { direction: 'stable', Icon: TrendingFlatIcon };
  }

  const trendLower = trend.toLowerCase();

  if (trendLower.includes('up') || trendLower.startsWith('+') || trendLower.includes('increas')) {
    return { direction: 'up', Icon: TrendingUpIcon };
  }

  if (trendLower.includes('down') || trendLower.startsWith('-') || trendLower.includes('decreas')) {
    return { direction: 'down', Icon: TrendingDownIcon };
  }

  return { direction: 'stable', Icon: TrendingFlatIcon };
}

/**
 * StatCardSection Component
 *
 * Displays key metrics in a card-based grid layout.
 * Each card shows a label, large value, and optional trend indicator.
 *
 * @param {object} props - Component props
 * @param {object} props.content - Stat cards content
 * @param {Array} props.content.stats - Array of stat objects
 * @param {string} props.content.stats[].label - Label for the metric
 * @param {string} props.content.stats[].value - Main value to display (large)
 * @param {string} [props.content.stats[].trend] - Optional trend indicator (e.g., '+12%', 'stable', 'up')
 * @param {string} [props.content.stats[].secondary] - Optional secondary text below value
 *
 * @example
 * <StatCardSection content={{
 *   stats: [
 *     { label: 'Market Size', value: '$2.5B', trend: '+12%' },
 *     { label: 'Growth Rate', value: '15%', trend: '+2%' },
 *     { label: 'Top Competitors', value: '8', trend: 'stable' }
 *   ]
 * }} />
 */
function StatCardSection({ content }) {
  if (!content || !content.stats || content.stats.length === 0) {
    return null;
  }

  const { stats } = content;

  return (
    <StatsGrid>
      {stats.map((stat, index) => {
        const { label, value, trend, secondary } = stat;
        const { direction, Icon: TrendIcon } = getTrendInfo(trend);

        return (
          <StatCard key={index}>
            <Text color="secondary" size="sm" weight="medium">
              {label}
            </Text>
            <StatValue>{value}</StatValue>
            {secondary && (
              <Text color="secondary" size="xs">
                {secondary}
              </Text>
            )}
            {trend && (
              <TrendWrapper trendDirection={direction}>
                <TrendIcon size="small" />
                <Text size="sm" weight="medium">
                  {trend}
                </Text>
              </TrendWrapper>
            )}
          </StatCard>
        );
      })}
    </StatsGrid>
  );
}

StatCardSection.propTypes = {
  content: PropTypes.shape({
    stats: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
        trend: PropTypes.string,
        secondary: PropTypes.string
      })
    ).isRequired
  }).isRequired
};

export default StatCardSection;
